import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, type PopoverState } from 'react-tiny-popover';
import type {
  SavedFilterView,
  SavedFilterViews,
} from '~/hooks/useSavedFilterViews';
import { FilterSelector } from './FilterSelector';
import { FilterToggleButton } from './FilterToggleButton';
import { popoverMaxHeight } from './popoverMaxHeight';
import { SaveFilterViewPanel } from './SaveFilterViewPanel';

type FilterPanelSlots = {
  maxHeight: number | undefined;
  bottomContent: ReactNode;
};

type SavedViewsFilterControlsProps<TRow> = {
  readonly namespace: string;
  /** Prefix of the `title` key and the `views.*` label keys. */
  readonly keyPrefix: string;
  readonly views: SavedFilterViews<TRow>;
  readonly activeCount: number;
  readonly visibleRowCount?: number;
  readonly buttonTestId: string;
  readonly countTestId: string;
  readonly renderPanel: (slots: FilterPanelSlots) => ReactNode;
  readonly popoverAlign?: 'start' | 'end';
  readonly popoverZIndex?: string;
};

/**
 * The saved-view pills plus the filter button and its popover, with the save / update
 * footer under the page's filter panel.
 *
 * @example
 * <SavedViewsFilterControls
 *   namespace="inventory"
 *   keyPrefix="inventory.buy_list.filter"
 *   views={views}
 *   activeCount={activeRowCount(views.rows)}
 *   buttonTestId="buy-list-filter-button"
 *   countTestId="buy-list-filter-count"
 *   renderPanel={({ maxHeight, bottomContent }) => (
 *     <BuyListFilterPanel
 *       maxHeight={maxHeight}
 *       bottomContent={bottomContent}
 *       rows={views.rows}
 *       fieldOptions={fieldOptions}
 *       productOptions={products}
 *       isLoadingProducts={isLoadingProducts}
 *       onRowsChange={views.setRows}
 *     />
 *   )}
 * />
 */
export function SavedViewsFilterControls<TRow>({
  namespace,
  keyPrefix,
  views,
  activeCount,
  visibleRowCount = views.rows.length,
  buttonTestId,
  countTestId,
  renderPanel,
  popoverAlign = 'end',
  popoverZIndex = '50',
}: SavedViewsFilterControlsProps<TRow>) {
  const { t } = useTranslation(namespace);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const showSavePanel = visibleRowCount > 0 || views.editingView !== null;
  const viewsKey = (key: string) => `${keyPrefix}.views.${key}`;

  // Radix closes its own menu on select and the resulting pointer event would reach the
  // popover as an outside click, so opening is deferred one tick.
  const openForEditing = (view: SavedFilterView) => {
    setTimeout(() => {
      views.startEditing(view);
      setIsPanelOpen(true);
    }, 0);
  };

  const editingName = views.editingView ? views.editingView.name : null;

  return (
    <>
      <FilterSelector
        filters={views.savedViews}
        activeFilterId={views.activeViewId}
        onActivate={views.activateView}
        onClear={views.clearActiveView}
        isLoading={views.isLoadingViews}
        onRenameFilter={views.renameView}
        onEditFilter={openForEditing}
        onDeleteFilter={views.deleteView}
        deleteConfirmTitle={(name) =>
          t(viewsKey('delete_confirm_title'), { name })
        }
        deleteConfirmButton={t(viewsKey('delete_confirm_button'))}
        deleteCancelButton={t(viewsKey('delete_cancel_button'))}
        ariaLabelContainer={t(viewsKey('aria_container'))}
        ariaLabelRenameInput={t(viewsKey('aria_rename_input'))}
        ariaLabelFilterOptions={t(viewsKey('aria_view_options'))}
        menuRenameLabel={t(viewsKey('menu_rename_label'))}
        menuEditLabel={t(viewsKey('menu_edit_label'))}
        menuDeleteLabel={t(viewsKey('menu_delete_label'))}
      />
      <Popover
        isOpen={isPanelOpen}
        onClickOutside={() => setIsPanelOpen(false)}
        positions={['bottom', 'top']}
        align={popoverAlign}
        containerStyle={{ zIndex: popoverZIndex }}
        content={(popoverState: PopoverState) =>
          renderPanel({
            maxHeight: popoverMaxHeight(popoverState),
            bottomContent: showSavePanel ? (
              <SaveFilterViewPanel
                editingName={editingName}
                isBusy={views.isBusy}
                isCapReached={views.isSaveCapReached}
                onSave={views.saveView}
                onUpdate={views.updateEditedView}
                onCapErrorDismiss={views.dismissSaveCapError}
              />
            ) : undefined,
          })
        }
      >
        <FilterToggleButton
          label={t(`${keyPrefix}.title`)}
          activeCount={activeCount}
          isPanelOpen={isPanelOpen}
          onClick={() => setIsPanelOpen((wasOpen) => !wasOpen)}
          buttonTestId={buttonTestId}
          countTestId={countTestId}
        />
      </Popover>
    </>
  );
}
